import React, { useEffect, useRef } from "react";
import { Container } from "reactstrap";

const gaugeData = [
  { id: "g1", val: 111, max: 140, color: "#f90", label: "Oil Tank 1" },
  { id: "g2", val: 60,  max: 140, color: "#f90", label: "Oil Tank 2" },
  { id: "g3", val: 12,  max: 140, color: "#f90", label: "Oil Tank 3" },
  { id: "g4", val: 111, max: 140, color: "#f90", label: "Oil Tank 4" },
  { id: "g5", val: 60,  max: 140, color: "#f90", label: "Oil Tank 5" },
  { id: "g6", val: 60,  max: 140, color: "#f90", label: "Oil Tank 6" },
  { id: "g7", val: 77,  max: 140, color: "#5ab4f5", label: "Water Tank 1" },
  { id: "g8", val: 114, max: 140, color: "#5ab4f5", label: "Water Tank 2" },
  { id: "g9", val: 55,  max: 140, color: "#5ab4f5", label: "Water Tank 3" },
  { id: "g10", val: 77,  max: 140, color: "#5ab4f5", label: "Water Tank 4" },
  { id: "g11", val: 114, max: 140, color: "#5ab4f5", label: "Water Tank 5" },
  { id: "g12", val: 55,  max: 140, color: "#5ab4f5", label: "Water Tank 6" },
];

const oilTanks = [
  [
    { name: "HFO Tank 1",      pct: 30 },
    { name: "MDO Tank 1",      pct: 99 },
    { name: "Service Tank 1",  pct: 8  },
    { name: "Settling Tank 1", pct: 88 },
  ],
  [
    { name: "HFO Tank 2",      pct: 30 },
    { name: "MDO Tank 2",      pct: 99 },
    { name: "Service Tank 2",  pct: 8  },
    { name: "Settling Tank 2", pct: 88 },
  ],
];

const waterTanks = [
  [
    { name: "Fresh Water 1",   pct: 100 },
    { name: "Ballast Water 1", pct: 50  },
    { name: "Grey Water 1",    pct: 88  },
    { name: "Sewage Water 1",  pct: 5   },
  ],
  [
    { name: "Fresh Water 2",   pct: 100 },
    { name: "Ballast Water 2", pct: 50  },
    { name: "Grey Water 2",    pct: 88  },
    { name: "Sewage Water 2",  pct: 5   },
  ],
];

const tankLevels = [
  [15.5, 16.5, 20, 14.5],
  [15.5, 16.5, 20, 14.5],
  [15.5, 16.5, 20, 14.5],
  [15.5, 16.5, 20, 14.5],
];

function drawGauge(canvas, val, max, color) {
  const ctx = canvas.getContext("2d");
  const cx = 40, cy = 44, r = 32;
  const startAngle = Math.PI * 0.75;
  const endAngle = Math.PI * 2.25;
  const pct = Math.min(val / max, 1);
  const valAngle = startAngle + pct * (endAngle - startAngle);

  ctx.clearRect(0, 0, 80, 80);

  // Outer decorative ring
  ctx.beginPath();
  ctx.arc(cx, cy, r + 6, 0, Math.PI * 2);
  ctx.strokeStyle = "#ececec";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Track background
  ctx.beginPath();
  ctx.arc(cx, cy, r, startAngle, endAngle);
  ctx.strokeStyle = "#e8e8e8";
  ctx.lineWidth = 8;
  ctx.lineCap = "round";
  ctx.stroke();

  // Value arc
  ctx.beginPath();
  ctx.arc(cx, cy, r, startAngle, valAngle);
  ctx.strokeStyle = color;
  ctx.lineWidth = 8;
  ctx.lineCap = "round";
  ctx.stroke();

  // Tick marks
  for (let i = 0; i <= 12; i++) {
    const angle = startAngle + (i / 12) * (endAngle - startAngle);
    const isMain = i % 3 === 0;
    const inner = r - (isMain ? 13 : 9);
    const outer2 = r - 5;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(angle) * inner, cy + Math.sin(angle) * inner);
    ctx.lineTo(cx + Math.cos(angle) * outer2, cy + Math.sin(angle) * outer2);
    ctx.strokeStyle = isMain ? "#bbb" : "#ddd";
    ctx.lineWidth = isMain ? 1.5 : 0.8;
    ctx.stroke();
  }

  // Center circle bg
  ctx.beginPath();
  ctx.arc(cx, cy, 18, 0, Math.PI * 2);
  ctx.fillStyle = "#1c1c1e";
  ctx.fill();

  // Center circle border
  ctx.beginPath();
  ctx.arc(cx, cy, 18, 0, Math.PI * 2);
  ctx.strokeStyle = color + "55";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Value text
  ctx.fillStyle = color;
  // ctx.font = "bold 11px 'Courier New', monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(val), cx, cy);
}

function Gauge({ val, max, color, label }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (canvasRef.current) {
      drawGauge(canvasRef.current, val, max, color);
    }
  }, [val, max, color]);

  return (
    <div style={styles.gaugeWrap}>
      <canvas ref={canvasRef} width={80} height={80} />
      <span style={styles.gaugeLabel}>{label}</span>
    </div>
  );
}

function TankBar({ name, pct, type }) {
  const isOil = type === "oil";
  const badgeStyle = {
    ...styles.tankBadge,
    background: isOil ? "#1c1c1e" : "#0b2a45",
    color: isOil ? "#f90" : "#5ab4f5",
    border: `2px solid ${isOil ? "#f90" : "#5ab4f5"}`,
    fontSize: pct === 100 ? 9 : 11,
  };
  const barColor = isOil
    ? "linear-gradient(90deg, #c55 0%, #f90 100%)"
    : "linear-gradient(90deg, #1a6fdb 0%, #5ab4f5 100%)";

  return (
    <div style={styles.tankItem}>
      <div style={badgeStyle}>{pct}%</div>
      <div style={styles.tankInfo}>
        <div style={styles.tankName}>{name}</div>
        <div style={styles.barWrap}>
          <div style={{ ...styles.bar, width: `${pct}%`, background: barColor }} />
        </div>
        <div style={styles.tankDepth}>12.2 m</div>
      </div>
    </div>
  );
}

export default function Monitor() {
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
    <div style={styles.dash}>
      {/* Top Bar */}
      <div style={styles.topbar}>
       
        <div style={styles.nav}>
          {["MV Atlas Prime ▾", "🗺 Map", "👥 Crew", "📋 Reports", "🔔 Alerts"].map((item, i) => (
            <button key={i} style={{ ...styles.navBtn, ...(i === 1 ? styles.navBtnActive : {}) }}>
              {item}
            </button>
          ))}
        </div>
        <div style={styles.topInfo}>
          <div style={styles.infoBlock}>
            <span style={styles.infoLabel}>Uptime</span>
            <span style={styles.infoVal}>25/06/2024 03:27:35</span>
          </div>
          <div style={styles.infoBlock}>
            <span style={styles.infoLabel}>System Status</span>
            <span style={{ ...styles.infoVal, color: "#4caf50" }}>● Online</span>
          </div>
          <div style={styles.infoBlock}>
            <span style={styles.infoLabel}>Time UTC</span>
            <span style={styles.infoVal}>14:27:35</span>
          </div>
        </div>
      </div>

      {/* Gauge Rows */}
      <div style={styles.gaugeGrid}>
        {/* Oil Gauges Group 1 */}
        <div style={styles.gaugeGroup}>
          {gaugeData.slice(0, 3).map((g) => (
            <Gauge key={g.id} val={g.val} max={g.max} color={g.color} label={g.label} />
          ))}
        </div>
        {/* Oil Gauges Group 2 */}
        <div style={styles.gaugeGroup}>
          {gaugeData.slice(3, 6).map((g) => (
            <Gauge key={g.id} val={g.val} max={g.max} color={g.color} label={g.label} />
          ))}
        </div>
        {/* Water Gauges Group 1 */}
        <div style={styles.gaugeGroup}>
          {gaugeData.slice(6, 9).map((g) => (
            <Gauge key={g.id} val={g.val} max={g.max} color={g.color} label={g.label} />
          ))}
        </div>
        {/* Water Gauges Group 2 */}
        <div style={styles.gaugeGroup}>
          {gaugeData.slice(9, 12).map((g) => (
            <Gauge key={g.id} val={g.val} max={g.max} color={g.color} label={g.label} />
          ))}
        </div>
      </div>

      {/* Tank Bars */}
      <div style={styles.tankGrid}>
        {oilTanks.map((col, ci) => (
          <div key={ci} style={styles.tankCol}>
            {col.map((t, ti) => (
              <TankBar key={ti} name={t.name} pct={t.pct} type="oil" />
            ))}
          </div>
        ))}
        {waterTanks.map((col, ci) => (
          <div key={ci} style={styles.tankCol}>
            {col.map((t, ti) => (
              <TankBar key={ti} name={t.name} pct={t.pct} type="water" />
            ))}
          </div>
        ))}
      </div>

      {/* Tank Levels Table */}
      <div style={styles.levelsBox}>
        <div style={styles.levelsTitle}>TANK LEVELS</div>
        <div style={styles.levelsGrid}>
          {tankLevels.map((col, ci) =>
            col.map((val, ri) => {
              const num = ci * 4 + ri + 1;
              return (
                <div key={num} style={styles.levelRow}>
                  <span style={styles.levelName}>TANK LEVEL {num}</span>
                  <span style={styles.levelVal}>{val} m</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {[
          { color: "#1a6fdb", label: "Main Engine" },
          { color: "#4caf50", label: "Auxiliary Engine" },
          { color: "#f7c300", label: "Auxiliary Boiler" },
          { color: "#888",    label: "Power Management System" },
          { color: "#5ab4f5", label: "Tank Level Monitoring" },
        ].map((item, i) => (
          <div key={i} style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: item.color }} />
            <span style={styles.legendLabel}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
    </Container>
    </div>
    </React.Fragment>
  );
}

const styles = {
  dash: {
    background: "#ffffff",
    padding: 16,
    // fontFamily: "'Courier New', Courier, monospace",
    minHeight: "100vh",
    boxSizing: "border-box",
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    // background: "#111111",
    color: "#fff",
    padding: "10px 18px",
    borderRadius: 10,
    marginBottom: 14,
  },
  logo: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: 2,
    color: "#fff",
  },
  nav: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  navBtn: {
    background: "transparent",
    border: "1px solid #444",
    color: "#bbb",
    padding: "4px 12px",
    borderRadius: 6,
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  navBtnActive: {
    background: "#1a6fdb",
    borderColor: "#1a6fdb",
    color: "#fff",
  },
  topInfo: {
    display: "flex",
    gap: 20,
  },
  infoBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  infoLabel: {
    fontSize: 9,
    color: "#888",
    letterSpacing: 0.5,
  },
  infoVal: {
    fontSize: 11,
    color: "#fff",
    fontWeight: 600,
  },
  gaugeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 10,
    marginBottom: 12,
  },
  gaugeGroup: {
    background: "#f8f8f8",
    border: "1px solid #eaeaea",
    borderRadius: 10,
    padding: "10px 6px",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "flex-start",
     background: "linear-gradient(160deg, #dadada 0%, #ffffff 100%)",
  border: "1px solid rgba(255,255,255,0.07)",
  borderRadius: 14,
  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 2px 8px rgba(255, 255, 255, 0.3)",
  },
  gaugeWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  gaugeLabel: {
    fontSize: 8.5,
    color: "#666",
    marginTop: 3,
    textAlign: "center",
    letterSpacing: 0.3,
  },
  tankGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 10,
    marginBottom: 12,
  },
  tankCol: {
    background: "#f8f8f8",
    border: "1px solid #e4e4e4",
    borderRadius: 10,
    padding: 10,
  },
  tankItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  tankBadge: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    flexShrink: 0,
    // fontFamily: "inherit",
  },
  tankInfo: {
    flex: 1,
    minWidth: 0,
  },
  tankName: {
    fontSize: 10,
    color: "#333",
    fontWeight: 600,
    marginBottom: 3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  barWrap: {
    height: 7,
    background: "#ddd",
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 2,
  },
  bar: {
    height: "100%",
    borderRadius: 4,
  },
  tankDepth: {
    fontSize: 9,
    color: "#999",
    textAlign: "right",
  },
  levelsBox: {
    background: "#f8f8f8",
    border: "1px solid #e4e4e4",
    borderRadius: 10,
    padding: "10px 14px",
    marginBottom: 12,
  },
  levelsTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#333",
    letterSpacing: 2,
    marginBottom: 8,
  },
  levelsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "3px 16px",
  },
  levelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 10,
    padding: "2px 0",
  },
  levelName: {
    color: "#888",
    fontSize: 9,
    letterSpacing: 0.4,
  },
  levelVal: {
    background: "#e6e6e6",
    borderRadius: 3,
    padding: "1px 6px",
    fontSize: 10,
    fontWeight: 700,
    color: "#333",
    minWidth: 40,
    textAlign: "center",
  },
  legend: {
    display: "flex",
    gap: 18,
    flexWrap: "wrap",
    padding: "6px 2px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 3,
    flexShrink: 0,
  },
  legendLabel: {
    fontSize: 10,
    color: "#555",
  },
};